/**
 * Das Angebot, wie es der Kunde bekommt (PV-L02).
 *
 * Ausgabe als PDF heisst hier: eine Druckansicht, die der Browser zu PDF macht.
 * Das spart eine zweite Rechenkette auf dem Server — und damit die Gefahr, dass
 * im PDF eine andere Modulanzahl steht als am Tablet. Gedruckt wird genau der
 * eingefrorene Stand aus dem Angebot, nicht der aktuelle Projektstand.
 *
 * „Der Kunde soll sehen, was geplant wurde" — deshalb steht die Anlagenuebersicht
 * mit Dachbild und Belegung oben und nicht im Anhang.
 */

import { huellkasten, type Modulplatz } from '@pv/geometrie';

import type { Planung } from '../planung.ts';
import { platzSchluessel } from '../planung.ts';
import { tagAnzeige } from '../datum.ts';
import type { Angebotszeile, Flaeche, Projekt } from '../typen.ts';

interface Eigenschaften {
  angebot: Angebotszeile;
  /** Zeichnung aus dem laufenden Projekt — die Geometrie aendert sich selten. */
  flaechen: Flaeche[];
  planungen: Map<string, Planung>;
  vorgaben: { hinweis?: string };
  onSchliessen: () => void;
}

interface Position {
  bezeichnung: string;
  menge: number;
  einheit: string;
  einzelpreis?: number | null;
}

export function Angebotsdruck(e: Eigenschaften): React.JSX.Element {
  const stand = e.angebot.stand as {
    projekt?: Projekt;
    material?: { pauschal?: Position[]; summeVerkauf?: number };
    wirtschaftlichkeit?: {
      leistungKwp?: number;
      ertragJahr?: number;
      investitionEuro?: number;
      amortisationJahre?: number | null;
      ersparnisErstesJahrEuro?: number;
      einspeiseerloesErstesJahrEuro?: number;
      bilanz?: { eigenverbrauchsanteil: number; autarkiegrad: number };
      rechenweg?: Array<{ groesse: string; wert: string; herkunft: string }>;
    };
    stammdatenstand?: { strahlungsquelle?: string };
  };

  const projekt = stand.projekt;
  const positionen = stand.material?.pauschal ?? [];
  const w = stand.wirtschaftlichkeit;

  return (
    <div className="druckrahmen" onClick={e.onSchliessen}>
      <div className="druckblatt" onClick={(ereignis) => ereignis.stopPropagation()}>
        <div className="nurBildschirm knopfreihe" style={{ marginBottom: '1.5rem' }}>
          <button className="wichtig" onClick={() => window.print()}>
            Drucken oder als PDF sichern
          </button>
          <button onClick={e.onSchliessen}>Schließen</button>
        </div>

        <header style={{ borderBottom: '1px solid var(--rand)', paddingBottom: '1rem' }}>
          <p className="marke">Wania GmbH · Gewerbestraße 1 · 3623 Kottes</p>
          <h1>Angebot {e.angebot.nummer}</h1>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            {e.angebot.variante} · {tagAnzeige(e.angebot.datum)}
            {e.angebot.gueltig_bis && ` · gültig bis ${tagAnzeige(e.angebot.gueltig_bis)}`}
          </p>
        </header>

        {projekt && (
          <section style={{ margin: '1.5rem 0' }}>
            <p className="marke">Für</p>
            <p style={{ margin: '0.3rem 0' }}>
              {projekt.kunde_name}
              <br />
              {[projekt.strasse, `${projekt.plz ?? ''} ${projekt.ort ?? ''}`.trim()]
                .filter(Boolean)
                .join(', ')}
              {projekt.zaehlpunktnummer && (
                <>
                  <br />
                  Zählpunkt {projekt.zaehlpunktnummer}
                </>
              )}
            </p>
          </section>
        )}

        {/* --- Anlagenuebersicht mit Dachbild ---------------------------- */}
        <section>
          <h2>Die geplante Anlage</h2>
          <dl className="zahlenblock">
            <div>
              <dt>Leistung</dt>
              <dd>{(w?.leistungKwp ?? 0).toFixed(2)} kWp</dd>
            </div>
            <div>
              <dt>Ertrag</dt>
              <dd>{Math.round(w?.ertragJahr ?? 0).toLocaleString('de-AT')} kWh/Jahr</dd>
            </div>
            {w?.bilanz && (
              <>
                <div>
                  <dt>Eigenverbrauch</dt>
                  <dd>{Math.round(w.bilanz.eigenverbrauchsanteil * 100)} %</dd>
                </div>
                <div>
                  <dt>Autarkie</dt>
                  <dd>{Math.round(w.bilanz.autarkiegrad * 100)} %</dd>
                </div>
              </>
            )}
          </dl>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '1rem 0' }}>
            {e.flaechen.map((flaeche) => {
              const planung = e.planungen.get(flaeche.id);
              if (!planung || planung.gesetzt.length === 0) return null;
              return (
                <figure key={flaeche.id} style={{ margin: 0, flex: '1 1 12rem' }}>
                  <Dachbild planung={planung} />
                  <figcaption style={{ fontSize: '0.72rem', color: 'var(--grau)' }}>
                    {flaeche.name} · {planung.zahlen.anzahl} Module ·{' '}
                    {planung.kennzahlen.neigungGrad}° · {planung.kennzahlen.azimutGrad.toFixed(0)}°
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </section>

        {/* --- Positionen ------------------------------------------------ */}
        <section>
          <h2>Leistungen</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr>
                <th style={druckKopf}>Position</th>
                <th style={{ ...druckKopf, textAlign: 'right' }}>Betrag</th>
              </tr>
            </thead>
            <tbody>
              {positionen.map((position, index) => (
                <tr key={index}>
                  <td style={druckZelle}>{position.bezeichnung}</td>
                  <td style={{ ...druckZelle, textAlign: 'right' }}>
                    {position.einzelpreis == null ? 'auf Anfrage' : euro(position.einzelpreis)}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ ...druckZelle, borderTop: '1px solid var(--rand)', fontWeight: 600 }}>
                  Summe netto
                </td>
                <td
                  style={{
                    ...druckZelle,
                    borderTop: '1px solid var(--rand)',
                    textAlign: 'right',
                    fontWeight: 600,
                  }}
                >
                  {euro(stand.material?.summeVerkauf ?? e.angebot.summe_netto)}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* --- Wirtschaftlichkeit ---------------------------------------- */}
        {w && (
          <section style={{ marginTop: '1.5rem' }}>
            <h2>Was die Anlage bringt</h2>
            <dl className="zahlenblock">
              <div>
                <dt>Ersparnis im ersten Jahr</dt>
                <dd>{euro(w.ersparnisErstesJahrEuro)}</dd>
              </div>
              <div>
                <dt>Einspeiseerlös</dt>
                <dd>{euro(w.einspeiseerloesErstesJahrEuro)}</dd>
              </div>
              <div>
                <dt>Investition</dt>
                <dd>{euro(w.investitionEuro)}</dd>
              </div>
              <div>
                <dt>Amortisation</dt>
                <dd>
                  {w.amortisationJahre == null
                    ? 'über den Betrachtungszeitraum hinaus'
                    : `${w.amortisationJahre.toFixed(1)} Jahre`}
                </dd>
              </div>
            </dl>

            {/* PV-K10: Der Rechenweg steht im Angebot, nicht im Anhang. */}
            {w.rechenweg && (
              <>
                <h3 style={{ marginTop: '1rem' }}>Wie diese Zahlen zustande kommen</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <tbody>
                    {w.rechenweg.map((zeile) => (
                      <tr key={zeile.groesse}>
                        <td style={{ ...druckZelle, width: '30%' }}>{zeile.groesse}</td>
                        <td style={{ ...druckZelle, width: '25%' }}>{zeile.wert}</td>
                        <td style={{ ...druckZelle, color: 'var(--grau)' }}>{zeile.herkunft}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>
        )}

        <footer style={{ marginTop: '2rem', fontSize: '0.75rem', color: 'var(--grau)' }}>
          <p>
            {e.vorgaben.hinweis ??
              'Preise freibleibend. Die Ertragsprognose ist eine Abschätzung, keine Zusage.'}
          </p>
          <p>
            Ertragsgrundlage: {stand.stammdatenstand?.strahlungsquelle ?? 'unbekannt'}. Kartenbild:
            Datenquelle basemap.at.
          </p>
        </footer>
      </div>
    </div>
  );
}

/** Die Belegung als kleine Zeichnung — dasselbe Bild wie im Stringplan. */
function Dachbild({ planung }: { planung: Planung }): React.JSX.Element {
  const alle = planung.gesetzt.flatMap((platz: Modulplatz) => platz.ecken);
  if (alle.length === 0) return <svg />;
  const kasten = huellkasten(alle);
  const rand = 0.4;

  return (
    <svg
      viewBox={`${kasten.minX - rand} ${kasten.minY - rand} ${kasten.maxX - kasten.minX + 2 * rand} ${
        kasten.maxY - kasten.minY + 2 * rand
      }`}
      style={{ width: '100%', height: 'auto', border: '1px solid var(--linie)' }}
    >
      <g transform={`translate(0, ${2 * kasten.minY + (kasten.maxY - kasten.minY)}) scale(1, -1)`}>
        {planung.gesetzt.map((platz: Modulplatz) => {
          const kante = huellkasten(platz.ecken);
          return (
            <rect
              key={platzSchluessel(platz)}
              x={kante.minX}
              y={kante.minY}
              width={kante.maxX - kante.minX}
              height={kante.maxY - kante.minY}
              fill="#1f3a5f"
              stroke="#fff"
              strokeWidth={0.02}
            />
          );
        })}
      </g>
    </svg>
  );
}

const druckKopf: React.CSSProperties = {
  textAlign: 'left',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  color: 'var(--grau)',
  fontWeight: 300,
  borderBottom: '1px solid var(--rand)',
  padding: '0.4rem 0.5rem 0.4rem 0',
};

const druckZelle: React.CSSProperties = {
  borderBottom: '1px solid var(--linie)',
  padding: '0.45rem 0.5rem 0.45rem 0',
  verticalAlign: 'top',
};

function euro(betrag: number | null | undefined): string {
  if (betrag == null) return '—';
  return betrag.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
}
